import { MUIcon } from '@platform/shared/ui';
import { PeTypes } from '@platform/types';
import { Helpers } from '@platform/utils';
import { QueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { SnackbarActionButton } from '../components/shared/Snackbars';
import JobTypes from '../enums/jobTypes';
import { INotification } from '../hooks/useSnackbars';
import { generateLink } from '../LinkGenerator';

const handleJobType = async (
  notification: PeTypes.GeoBufferJobNotification,
  queryClient: QueryClient,
  notify: (n: INotification) => () => void,
  activeUserId: number,
  activeWorkspaceId: number
) => {
  const jobType = notification.payload.jobInfo.jobType;
  const datasetId = notification.payload.jobInfo.metadata.datasetUuid;
  const notificationWorkspaceId = notification.payload.jobInfo.ownerId;
  const notificationUserId = notification.payload.jobInfo.userId;
  const statusCode = notification.payload.jobInfo.statusCode;

  const notificationHandler = (notificationData: INotification) => {
    if (notificationUserId === activeUserId && notificationWorkspaceId === activeWorkspaceId) {
      // only notify if same user and workspace
      return notify(notificationData);
    }
  };

  switch (jobType) {
    case JobTypes.IMPORT: {
      // Dataset added -> re-fetch datasets
      await queryClient.invalidateQueries({ queryKey: ['datasets', notificationWorkspaceId] });
      await queryClient.invalidateQueries({ queryKey: ['workspace-statistics', notificationWorkspaceId] });
      const errorPayload = notification.payload.jobInfo.results[0]?.payload?.error;

      if (errorPayload) {
        // an error occurred
        notificationHandler({
          hideAfterSec: 10,
          dismissible: false,
          content: `Import of ${errorPayload.datasetName} failed. Reason: ${errorPayload.message}`,
          icon: <MUIcon name="close" />,
        });
      } else {
        const successPayload = notification.payload.jobInfo.results[0]?.payload?.success;
        const newDatasetTitle = successPayload?.targetDatasetName;
        notificationHandler({
          hideAfterSec: 10,
          content: `Imported “${newDatasetTitle}”`,
          action: (
            <Link
              to={generateLink('dataset', {
                workspaceId: activeWorkspaceId,
                datasetId: successPayload.targetDatasetUuid,
              })}
            >
              <SnackbarActionButton>View Dataset</SnackbarActionButton>
            </Link>
          ),
        });
      }
      break;
    }
    case JobTypes.EXPORT: {
      if (notificationUserId === activeUserId && notificationWorkspaceId === activeWorkspaceId) {
        // for exports, we need the download link
        const result = notification.payload.jobInfo.results[0].payload.success;
        const link = document.createElement('a');
        link.setAttribute('href', result.createdDownloadLink);
        link.style.display = 'none';
        link.setAttribute('target', '_blank');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
      break;
    }
    case JobTypes.GEOBUFFER_BULK_CREATE_NEW_FROM_EXISTING_DATASET: {
      await queryClient.invalidateQueries({ queryKey: ['datasets', notificationWorkspaceId] });
      await queryClient.invalidateQueries({ queryKey: ['workspace-statistics', notificationWorkspaceId] });
      const errorPayload = notification.payload.jobInfo.results[0]?.payload?.error;

      if (errorPayload) {
        // an error occurred
        notificationHandler({
          hideAfterSec: 10,
          content: errorPayload.message,
          icon: <MUIcon name="close" />,
        });
      } else {
        const successPayload = notification.payload.jobInfo.results[0]?.payload?.success;
        const newDatasetTitle = successPayload?.targetDatasetName;
        const closeHandle = notificationHandler({
          hideAfterSec: 10,
          content: `Created “${newDatasetTitle}”.`,
          action: (
            <Link
              to={generateLink('dataset', {
                workspaceId: activeWorkspaceId,
                datasetId: successPayload.targetDatasetUuid,
              })}
              onClick={() => closeHandle?.()}
            >
              <SnackbarActionButton>View Dataset</SnackbarActionButton>
            </Link>
          ),
        });
      }
      break;
    }

    case JobTypes.GEOBUFFER_BULK_DELETE_DATASETS_AND_REMOVE_GEO:
      // Dataset moved to trash -> re-fetch datasets
      queryClient.invalidateQueries({ queryKey: ['datasets', notificationWorkspaceId] });
      queryClient.invalidateQueries({ queryKey: ['workspace-statistics', notificationWorkspaceId] });
      break;
    case JobTypes.GEOBUFFER_UPDATE_COLUMN: {
      await queryClient.invalidateQueries({ queryKey: ['dataset-columns', datasetId] });
      await queryClient.invalidateQueries({ queryKey: ['dataset', datasetId] });
      await queryClient.invalidateQueries({ queryKey: ['datasets', notificationWorkspaceId] });

      notificationHandler({
        content: `Column metadata updated.`,
      });
      break;
    }
    case JobTypes.GEOBUFFER_CREATE_COLUMN:
    case JobTypes.GEOBUFFER_DUPLICATE_COLUMN: {
      await queryClient.invalidateQueries({ queryKey: ['dataset-columns', datasetId] });
      await queryClient.invalidateQueries({ queryKey: ['dataset', datasetId] });

      const newColumnTitle = notification.payload.jobInfo.metadata.columnName;
      notificationHandler({ content: `Created column “${newColumnTitle}” at the end of the table.` });
      break;
    }
    case JobTypes.GEOBUFFER_CREATE_COLUMNS_PROPERTIES:
      queryClient.invalidateQueries({ queryKey: ['dataset-data', datasetId] });
      queryClient.invalidateQueries({ queryKey: ['dataset', datasetId] });
      break;
    case JobTypes.GEOBUFFER_PUBLISH_DATASET_ARTIFACTS:
      if (statusCode === 3) {
        // COMPLETED
        await Helpers.sleep(5); // 5 seconds is the backend pool interval for job completeness. this sleep will keep invalidations in sync with backend

        await queryClient.invalidateQueries({ queryKey: ['datasets', notificationWorkspaceId] });
        await queryClient.invalidateQueries({ queryKey: ['dataset', datasetId] });
        await queryClient.invalidateQueries({ queryKey: ['abacus-columns', datasetId] });
        await queryClient.invalidateQueries({ queryKey: ['dataset-last-published-state', datasetId] });
        // show success
        notificationHandler({
          content: <span>Successfully published dataset changes.</span>,
          icon: <MUIcon name="check" />,
        });
      } else {
        // show failed message
        notificationHandler({
          content: <span>Failed to publish dataset changes. Please try again.</span>,
          icon: <MUIcon name="warning" />,
        });
      }
      break;
    case JobTypes.GEOBUFFER_COMBINE_COLUMNS: {
      await queryClient.invalidateQueries({ queryKey: ['dataset-columns', datasetId] });
      await queryClient.invalidateQueries({ queryKey: ['dataset-data', datasetId] });
      await queryClient.invalidateQueries({ queryKey: ['dataset', datasetId] });

      const combinedCols = notification.payload.jobInfo.metadata.preparedNotificationInfo?.columnsNames ?? [];
      const newColumnTitle = combinedCols.join('_');

      notificationHandler({
        content: `Created combined column “${newColumnTitle}” at the end of the table.`,
        dismissible: true,
      });
      break;
    }
    case JobTypes.GEOBUFFER_BULK_HARD_DELETE_DATASETS:
      await queryClient.invalidateQueries({ queryKey: ['datasets', notificationWorkspaceId] });
      await queryClient.invalidateQueries({ queryKey: ['workspace-statistics', notificationWorkspaceId] });
      break;
    default:
      console.error(`Unknown action received: ${jobType}`);
  }
  return 0;
};

export default handleJobType;
